/**
 * Reconnexion au serveur de mise en relation (PeerJS) après une coupure :
 * onglet en arrière-plan, réseau qui change… La connexion P2P déjà établie
 * n'en dépend pas, mais un hôte qui attend son ami, OUI : sans serveur, son
 * lien devient « introuvable ». Dépendances injectées : testable sans réseau.
 */
export type SignalingState = 'online' | 'reconnecting' | 'stopped';

export const reconnectDelay = (attempt: number, baseMs = 1000, maxMs = 30_000): number =>
  Math.min(maxMs, baseMs * 2 ** attempt);

export interface ReconnectorDeps {
  reconnect(): void;
  schedule(ms: number, fn: () => void): () => void;
}

export interface Reconnector {
  readonly state: SignalingState;
  /** Le serveur est perdu (ou une tentative a échoué). */
  onDisconnected(): void;
  /** Connexion au serveur (r)établie. */
  onOpen(): void;
  /** La page redevient visible : inutile d'attendre la prochaine tentative. */
  onVisible(): void;
  onChange(listener: (state: SignalingState) => void): () => void;
  stop(): void;
}

export function createReconnector(deps: ReconnectorDeps): Reconnector {
  let state: SignalingState = 'online';
  let attempt = 0;
  let cancel: (() => void) | null = null;
  const listeners = new Set<(s: SignalingState) => void>();

  const setState = (s: SignalingState): void => {
    if (s === state) return;
    state = s;
    for (const l of listeners) l(s);
  };
  const clear = (): void => {
    cancel?.();
    cancel = null;
  };
  const tryNow = (): void => {
    clear();
    attempt++;
    deps.reconnect();
  };

  return {
    get state() {
      return state;
    },
    onDisconnected() {
      if (state === 'stopped' || cancel) return;
      setState('reconnecting');
      cancel = deps.schedule(reconnectDelay(attempt), () => {
        cancel = null;
        tryNow();
      });
    },
    onOpen() {
      if (state === 'stopped') return;
      clear();
      attempt = 0;
      setState('online');
    },
    onVisible() {
      // Seulement si une tentative ATTEND : pas de doublon avec une tentative en cours.
      if (cancel) tryNow();
    },
    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    stop() {
      clear();
      setState('stopped');
    },
  };
}
